import React, { useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { calculatePrice } from '../../utils/pricingLogic';

export default function StepResult({ data }) {
  const result = calculatePrice(data);
  const isGame = data.type === 'game';
  const isWetteren = data.location === 'wetteren';
  const hasInsightsProfiles = data.type === 'insights' && data.insightsProfiles === 'yes';
  const isFullDay = data.duration === 'hele_dag' || data.duration === '6u';

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://client.consolto.com/iframeApp/iframeApp.js';
    script.id = 'et-iframe';
    script.async = true;
    script.setAttribute('data-widgetId', '6915a0b7b7659900c32d4408');
    script.setAttribute('data-version', '0.5');
    script.setAttribute('data-test', 'false');
    document.body.appendChild(script);

    return () => {
      const existingScript = document.getElementById('et-iframe');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Gradient restored here as requested */}
      <div className="bg-gradient-to-br from-[#154A48] to-[#1a5d5b] rounded-2xl p-6 text-white shadow-xl">
        <h2 className="text-xl font-medium opacity-90 mb-1">Uw geschatte investering</h2>
        <div className="flex items-end gap-2 mb-4">
          <span className="text-4xl font-bold">€ {result.total.toLocaleString('nl-BE')}</span>
          <span className="text-sm opacity-80 mb-1">excl. 21% BTW</span>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2 text-left">
          <SafeIcon name="List" className="text-[#154A48]" />
          Prijsdetails
        </h3>
        <ul className="space-y-3">
          {result.breakdown.map((item, idx) => (
            <li key={idx} className="flex justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0">
              <span className="text-gray-600 text-left">{item.label}</span>
              <span className="font-medium text-gray-800">€ {item.amount.toLocaleString('nl-BE')}</span>
            </li>
          ))}
        </ul>
      </div>

      {isWetteren && (
        <div className="bg-white p-5 rounded-xl border border-[#154A48]/20 text-left">
          <div className="flex items-center gap-2 mb-3 text-[#154A48]">
            <SafeIcon name="Coffee" className="font-bold" />
            <h4 className="font-bold text-sm uppercase tracking-wide">Catering & Faciliteiten (Wetteren)</h4>
          </div>
          <p className="text-xs text-gray-600 mb-3">Inbegrepen in de prijs voor de locatie:</p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start gap-2 text-sm text-gray-700">
              <SafeIcon name="Check" className="text-[#154A48] mt-1 shrink-0 text-xs" />
              <span>Onbeperkt water, koffie en diverse theesoorten</span>
            </li>
            {isFullDay && (
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <SafeIcon name="Check" className="text-[#154A48] mt-1 shrink-0 text-xs" />
                <span>Verzorgde broodjeslunch (diverse belegde broodjes)</span>
              </li>
            )}
            <li className="flex items-start gap-2 text-sm text-gray-700">
              <SafeIcon name="Check" className="text-[#154A48] mt-1 shrink-0 text-xs" />
              <span>Lichtrijke vergaderruimte met alle nodige didactische middelen</span>
            </li>
          </ul>
        </div>
      )}

      {hasInsightsProfiles && (
        <div className="bg-white p-5 rounded-xl border border-[#154A48]/20 text-left">
          <div className="flex items-center gap-2 mb-3 text-[#154A48]">
            <SafeIcon name="Info" className="font-bold" />
            <h4 className="font-bold text-sm uppercase tracking-wide">Inbegrepen Insights Materiaal</h4>
          </div>
          <p className="text-xs text-gray-600 mb-3">Het pakket per deelnemer omvat:</p>
          <ul className="grid grid-cols-1 gap-2">
            {[
              'Persoonlijk Insights Discovery profiel',
              'Professioneel afgedrukt en ingebonden',
              'Individueel werkboek',
              'Zachte Insights Discovery blokken',
              'Teamwiel (visuele weergave van het team)'
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <SafeIcon name="Check" className="text-[#154A48] mt-1 shrink-0 text-xs" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white p-4 rounded-xl border border-gray-200 text-left">
        <div className="flex items-center gap-2 mb-2 text-gray-800">
          <SafeIcon name="Users" className="text-[#154A48]" />
          <h4 className="font-bold text-sm uppercase tracking-wide">Begeleiding & Kwaliteit</h4>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed">
          Deze prijs is gebaseerd op begeleiding door <strong>{isGame ? '2 trainers' : '1 trainer'}</strong>.
          {!isGame && " Voor grotere groepen bevelen we vaak een tweede trainer aan."}
        </p>
      </div>

      <div className="bg-white p-5 rounded-xl border border-gray-200 text-left">
        <div className="flex items-center gap-2 mb-2 text-[#154A48]">
          <SafeIcon name="TrendingDown" className="font-bold" />
          <h4 className="font-bold text-sm uppercase tracking-wide">Speciale Tarieven & Trajecten</h4>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">
          Heeft u een <strong>zeer grote groep</strong> of bent u een <strong>sociale instelling</strong>? Vraag een offerte op maat aan.
        </p>
        <a
          href="https://talentcoachatwork.com/vrijblijvende-offerte-aanvragen"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 text-[#154A48] font-semibold text-sm inline-flex items-center gap-1 hover:underline"
        >
          Vraag een offerte op maat aan <SafeIcon name="ArrowRight" className="text-xs" />
        </a>
      </div>

      <div className="space-y-6 pt-2 text-center">
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 text-left">
          <p className="text-sm text-gray-700 leading-relaxed">
            <strong>Let op:</strong> Dit is een algemene prijsindicatie. De definitieve prijs wordt bepaald in een officieel voorstel.
          </p>
        </div>
        
        <div className="py-4 border-t border-gray-100">
          <p className="text-[#154A48] font-bold mb-2">Heeft u vragen over deze prijs?</p>
          <p className="text-sm text-gray-600 mb-4">
            Start een gesprek via <strong>het chat-icoon</strong> rechtsonder.
          </p>
        </div>
      </div>
    </div>
  );
}